import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Cpu, Layers3, Plus, RadioTower, Settings2 } from "lucide-react";
import { useMyCompany } from "../../api/companies";
import { useCreateDevice, usePatchDeviceConfiguration } from "../../api/devices";
import { useDeviceModels, useDeviceTypes } from "../../api/deviceTypes";
import { extractErrorMessage } from "../../api/errors";
import { useGroups } from "../../api/groups";
import type { Device } from "../../api/types";
import { getRole, isActingAsCompany } from "../../auth/session";
import { ConfigFieldsForm } from "../../components/config/ConfigFieldsForm";
import { usesGatewayConfigPage } from "./gatewayConfig";
import {
  buildConfigOverrides,
  initialConfigValues,
  isValueOnlyPath,
  missingRequiredFields as findMissingRequiredFields,
  type ConfigValues,
} from "../../components/config/configForm";

export function DeviceCreatePage() {
  const navigate = useNavigate();
  const companyQuery = useMyCompany();
  const groupsQuery = useGroups({ counts: false });
  const deviceTypesQuery = useDeviceTypes();
  const createDevice = useCreateDevice();
  const patchDeviceConfiguration = usePatchDeviceConfiguration();
  const company = companyQuery.data?.data;

  const [imei, setImei] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [description, setDescription] = useState("");
  const [deviceTypeId, setDeviceTypeId] = useState("");
  const [model, setModel] = useState("");
  const [groupId, setGroupId] = useState("");
  const [configValues, setConfigValues] = useState<ConfigValues>({});
  // The SOS and button settings changed on this form. See isValueOnlyPath.
  const [changedPaths, setChangedPaths] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");
  const [createdDevice, setCreatedDevice] = useState<Device | null>(null);
  const [createdConfigWarning, setCreatedConfigWarning] = useState("");

  const groups = useMemo(
    () => [...(groupsQuery.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [groupsQuery.data?.data],
  );

  const deviceTypes = useMemo(
    () => [...(deviceTypesQuery.data?.data ?? [])].filter((type) => type.status !== "deprecated").sort((a, b) => a.name.localeCompare(b.name)),
    [deviceTypesQuery.data?.data],
  );

  const modelsQuery = useDeviceModels({ deviceTypeId: deviceTypeId || undefined, enabled: !!deviceTypeId });
  const deviceModels = useMemo(
    () => [...(modelsQuery.data?.data ?? [])].filter((entry) => entry.status !== "deprecated").sort((a, b) => a.name.localeCompare(b.name)),
    [modelsQuery.data?.data],
  );

  const selectedDeviceType = useMemo(
    () => deviceTypes.find((entry) => entry.deviceTypeId === deviceTypeId),
    [deviceTypes, deviceTypeId],
  );

  // A gateway reports its own running config on first check-in and that report
  // becomes its baseline - but only while it has no stored override. Writing
  // the schema defaults here would count as one, and the defaults (no server,
  // no sensors) would then be pushed over the unit's real settings. So its
  // configuration is edited from the settings page once it has reported.
  const reportsOwnConfig = usesGatewayConfigPage(selectedDeviceType?.deviceTypeId);

  // Deprecated fields stay in the schema so stored payloads still validate, but
  // must not be offered as something to set on a new device.
  const configFields = useMemo(
    () => (reportsOwnConfig ? [] : (selectedDeviceType?.fields ?? []).filter((field) => !field.deprecated)),
    [selectedDeviceType, reportsOwnConfig],
  );

  const missingRequired = useMemo(
    () => findMissingRequiredFields(configFields, configValues),
    [configValues, configFields],
  );

  const mustSelectCompanyFirst = getRole() === "super_admin" && !isActingAsCompany();

  useEffect(() => {
    setChangedPaths(new Set());
    if (!selectedDeviceType) {
      setConfigValues({});
      setModel("");
      return;
    }

    setConfigValues(initialConfigValues(configFields, [selectedDeviceType.defaultPayload]));
    setModel("");
  }, [selectedDeviceType, configFields]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setCreatedConfigWarning("");
    const trimmedDeviceName = deviceName.trim();

    try {
      const createResponse = await createDevice.mutateAsync({
        imei: imei.trim(),
        deviceName: trimmedDeviceName || undefined,
        contactNumber: contactNumber.trim() || undefined,
        deviceTypeId: deviceTypeId || undefined,
        model: model.trim() || undefined,
        description: description.trim() || undefined,
        groupId: groupId || undefined,
      });

      const created = createResponse.data;
      // The form opens on the device type's defaults and every value on it is
      // stored as this device's own. An SOS or button setting is stored only
      // if it was changed here: a default left as it was would be sent again
      // on every later save, over whatever a group configuration had set.
      const overrideFields = configFields.filter((field) => !isValueOnlyPath(field.path) || changedPaths.has(field.path));
      const overrides = buildConfigOverrides(overrideFields, configValues);

      if (selectedDeviceType && Object.keys(overrides).length > 0) {
        try {
          await patchDeviceConfiguration.mutateAsync({ imei: created.imei, overrides });
        } catch (configErr) {
          setCreatedConfigWarning(extractErrorMessage(configErr, "Device was created, but the configuration payload could not be saved."));
        }
      }

      setCreatedDevice(created);
      setImei("");
      setDeviceName("");
      setContactNumber("");
      setDescription("");
      setDeviceTypeId("");
      setModel("");
      setGroupId("");
      setConfigValues({});
      setChangedPaths(new Set());
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to create device"));
    }
  }

  function updateConfigValue(path: string, value: string) {
    setConfigValues((current) => ({ ...current, [path]: value }));
    if (isValueOnlyPath(path)) setChangedPaths((current) => new Set(current).add(path));
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/devices")}
        className="flex items-center gap-1 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
      >
        <ArrowLeft size={14} /> Back to devices
      </button>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-riot-gold-100 text-riot-gold-600 dark:bg-riot-gold-500/10 dark:text-riot-gold-400">
                <Plus size={20} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-riot-gold-600 dark:text-riot-gold-400">
                  Device onboarding
                </p>
                <h1 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">Add device</h1>
                <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-riot-navy-300">
                  Choose the device family first, then the matching hardware model. The form below adapts to the selected
                  device type so integration endpoints, phone routing, and other runtime settings stay aligned with the
                  actual Riot hardware.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">
                  <Building2 size={13} /> Company
                </div>
                <div className="font-medium text-gray-800 dark:text-riot-navy-100">
                  {company?.companyName ?? "Loading company..."}
                </div>
                {company && (
                  <div className="mt-1 font-mono text-[11px] text-gray-500 dark:text-riot-navy-400">{company.tenantId}</div>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">
                  <Cpu size={13} /> Device types
                </div>
                <div className="font-medium text-gray-800 dark:text-riot-navy-100">{deviceTypes.length}</div>
                <div className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">Riot-managed hardware families</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">
                  <RadioTower size={13} /> Models available
                </div>
                <div className="font-medium text-gray-800 dark:text-riot-navy-100">{deviceModels.length}</div>
                <div className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">Filtered by selected type</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">
                  <Layers3 size={13} /> Groups available
                </div>
                <div className="font-medium text-gray-800 dark:text-riot-navy-100">{groups.length}</div>
                <div className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">Optional assignment during creation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {mustSelectCompanyFirst && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Select a company before creating devices.</p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
            As a platform admin, use <span className="font-medium">Platform Admin &gt; Companies</span> and masquerade into a
            company first. That keeps device creation tied to the correct tenant.
          </p>
        </section>
      )}

      {createdDevice && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                Device {createdDevice.deviceName || createdDevice.imei} has been added.
              </p>
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                It has been created for {company?.companyName ?? "the active company"} and is ready for grouping, config rollout,
                and task assignment.
              </p>
              {createdConfigWarning && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  Device created, but configuration needs attention: {createdConfigWarning}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate("/devices")}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600"
              >
                Back to devices
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreatedDevice(null);
                  setCreatedConfigWarning("");
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
              >
                Add another
              </button>
            </div>
          </div>
        </section>
      )}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800"
      >
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Device identity</h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
            Inventory fields and runtime configuration stay separate. The device row captures identity; the configuration
            panel below captures type-specific operating parameters.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="device-imei" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              IMEI
            </label>
            <input
              id="device-imei"
              type="text"
              inputMode="numeric"
              value={imei}
              onChange={(e) => setImei(e.target.value.replace(/\D/g, ""))}
              placeholder="15-digit IMEI"
              required
              maxLength={15}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            />
          </div>

          <div>
            <label htmlFor="device-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              Device name
            </label>
            <input
              id="device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="Truck 17"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            />
          </div>

          <div>
            <label htmlFor="device-contact-number" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              Contact number <span className="font-normal text-gray-400 dark:text-riot-navy-400">(optional)</span>
            </label>
            <input
              id="device-contact-number"
              type="tel"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              placeholder="+27 82 000 0000"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            />
          </div>

          <div>
            <label htmlFor="device-type" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              Device type
            </label>
            <select
              id="device-type"
              value={deviceTypeId}
              onChange={(e) => setDeviceTypeId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            >
              <option value="">No type</option>
              {deviceTypes.map((entry) => (
                <option key={entry.deviceTypeId} value={entry.deviceTypeId}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="device-model" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              Model
            </label>
            <select
              id="device-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={!deviceTypeId}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            >
              <option value="">No model</option>
              {deviceModels.map((entry) => (
                <option key={entry.modelCode} value={entry.modelCode}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="device-description" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              Description
            </label>
            <textarea
              id="device-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional operational note, install context, or deployment detail."
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            />
          </div>

          <div>
            <label htmlFor="device-group" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              Group
            </label>
            <select
              id="device-group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
            >
              <option value="">No group yet</option>
              {groups.map((group) => (
                <option key={group.groupId} value={group.groupId}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-5 dark:border-riot-navy-700">
          <div className="mb-4 flex items-center gap-2">
            <Settings2 size={16} className="text-riot-gold-500" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Type-specific configuration</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                These values are stored through the device configuration path and validated against the selected device type.
              </p>
            </div>
          </div>

          {!selectedDeviceType ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-riot-navy-700 dark:text-riot-navy-300">
              Choose a device type to reveal its configuration fields.
            </div>
          ) : reportsOwnConfig ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-riot-navy-700 dark:text-riot-navy-300">
              This device reports its own configuration when it first checks in, and that becomes its baseline. Edit it
              from the device&apos;s shadow settings once it has reported.
            </div>
          ) : configFields.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-riot-navy-700 dark:text-riot-navy-300">
              This device type does not define any configuration fields yet.
            </div>
          ) : (
            <ConfigFieldsForm
              fields={configFields}
              values={configValues}
              onChange={updateConfigValue}
              idPrefix="device-config"
            />
          )}
        </div>

        {missingRequired.length > 0 && selectedDeviceType && (
          <p className="mt-4 text-xs text-amber-700 dark:text-amber-300">
            Fill the required {missingRequired.length === 1 ? "field" : "fields"} for {selectedDeviceType.name} before creating the device.
          </p>
        )}

        {error && <p className="mt-4 text-xs text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate("/devices")}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              createDevice.isPending ||
              patchDeviceConfiguration.isPending ||
              mustSelectCompanyFirst ||
              !imei.trim() ||
              missingRequired.length > 0
            }
            className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {createDevice.isPending || patchDeviceConfiguration.isPending ? "Creating..." : "Create device"}
          </button>
        </div>
      </form>
    </div>
  );
}
